import React, { useCallback } from 'react';
import {
  MousePointer2,
  Move,
  RotateCw,
  Maximize2,
  Hand,
  Grid3X3,
  Magnet,
  Eye,
  Ruler,
  ChevronDown,
  Home,
  Play,
  Pause,
  Square,
} from 'lucide-react';
import { useSceneViewStore } from '../../store/sceneViewStore';
import { useEditorStore } from '../../store/editorStore';
import { useLivePreviewStore } from '../../store/livePreviewStore';
import { usePanelStore } from '../../store/panelStore';
import { TransformTool, ZOOM_PRESETS } from '../../../core/types/scene.types';
import { cn } from '../../utils/cn';

interface ToolButtonProps {
  icon: React.ReactNode;
  tooltip: string;
  shortcut?: string;
  active?: boolean;
  onClick: () => void;
}

const ToolButton: React.FC<ToolButtonProps> = ({
  icon,
  tooltip,
  shortcut,
  active = false,
  onClick,
}) => {
  return (
    <button
      className={cn(
        'p-1.5 rounded transition-colors',
        active
          ? 'bg-blue-600 text-white'
          : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
      )}
      onClick={onClick}
      title={shortcut ? `${tooltip} (${shortcut})` : tooltip}
    >
      {icon}
    </button>
  );
};

interface ToggleButtonProps {
  icon: React.ReactNode;
  tooltip: string;
  active: boolean;
  onClick: () => void;
}

const ToggleButton: React.FC<ToggleButtonProps> = ({
  icon,
  tooltip,
  active,
  onClick,
}) => {
  return (
    <button
      className={cn(
        'p-1.5 rounded transition-colors',
        active
          ? 'text-blue-400 bg-blue-600/20'
          : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700'
      )}
      onClick={onClick}
      title={tooltip}
    >
      {icon}
    </button>
  );
};

const ToolbarSeparator: React.FC = () => (
  <div className="w-px h-6 bg-zinc-700 mx-1" />
);

export const SceneToolbar: React.FC = () => {
  const {
    activeTool,
    setActiveTool,
    viewport,
    setZoom,
    resetView,
    toggleGrid,
    toggleSnap,
    toggleGizmos,
    toggleRulers,
  } = useSceneViewStore();

  const {
    playMode,
    play: editorPlay,
    pause: editorPause,
    stop: editorStop,
  } = useEditorStore();

  const {
    play: livePreviewPlay,
    pause: livePreviewPause,
    stop: livePreviewStop,
  } = useLivePreviewStore();

  const { showPanel, floatPanel, panels } = usePanelStore();

  const isPlaying = playMode === 'playing';
  const isPaused = playMode === 'paused';

  const [zoomDropdownOpen, setZoomDropdownOpen] = React.useState(false);

  const tools: Array<{
    tool: TransformTool;
    icon: React.ReactNode;
    tooltip: string;
    shortcut: string;
  }> = [
    { tool: 'select', icon: <MousePointer2 size={18} />, tooltip: 'Select', shortcut: 'V' },
    { tool: 'move', icon: <Move size={18} />, tooltip: 'Move', shortcut: 'W' },
    { tool: 'rotate', icon: <RotateCw size={18} />, tooltip: 'Rotate', shortcut: 'E' },
    { tool: 'scale', icon: <Maximize2 size={18} />, tooltip: 'Scale', shortcut: 'R' },
    { tool: 'pan', icon: <Hand size={18} />, tooltip: 'Pan', shortcut: 'Q' },
  ];

  const handleToolClick = useCallback(
    (tool: TransformTool) => {
      setActiveTool(tool);
    },
    [setActiveTool]
  );

  const handleZoomSelect = useCallback(
    (zoom: number) => {
      setZoom(zoom);
      setZoomDropdownOpen(false);
    },
    [setZoom]
  );

  const handleResetView = useCallback(() => {
    resetView();
  }, [resetView]);

  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      editorPause();
      livePreviewPause();
    } else {
      const livePreviewPanel = panels['live-preview'];
      if (!livePreviewPanel?.visible) {
        showPanel('live-preview');
      } else if (livePreviewPanel.state === 'minimized') {
        floatPanel('live-preview');
      }
      editorPlay();
      livePreviewPlay();
    }
  }, [isPlaying, editorPause, livePreviewPause, editorPlay, livePreviewPlay, showPanel, floatPanel, panels]);

  const handleStop = useCallback(() => {
    editorStop();
    livePreviewStop();
  }, [editorStop, livePreviewStop]);

  const zoomPercentage = Math.round(viewport.zoom * 100);

  return (
    <div className="flex items-center justify-between h-9 flex-shrink-0 bg-zinc-800 border-b border-zinc-700 px-2">
      {/* Left: Transform Tools */}
      <div className="flex items-center gap-0.5">
        {tools.map((item) => (
          <ToolButton
            key={item.tool}
            icon={item.icon}
            tooltip={item.tooltip}
            shortcut={item.shortcut}
            active={activeTool === item.tool}
            onClick={() => handleToolClick(item.tool)}
          />
        ))}
      </div>

      {/* Center: Toggle Options */}
      <div className="flex items-center gap-0.5">
        <ToggleButton
          icon={<Grid3X3 size={18} />}
          tooltip="Toggle Grid"
          active={viewport.showGrid}
          onClick={toggleGrid}
        />
        <ToggleButton
          icon={<Magnet size={18} />}
          tooltip="Toggle Snap to Grid"
          active={viewport.snapToGrid}
          onClick={toggleSnap}
        />
        <ToggleButton
          icon={<Eye size={18} />}
          tooltip="Toggle Gizmos"
          active={viewport.showGizmos}
          onClick={toggleGizmos}
        />
        <ToggleButton
          icon={<Ruler size={18} />}
          tooltip="Toggle Rulers"
          active={viewport.showRulers}
          onClick={toggleRulers}
        />
      </div>

      {/* Play Controls */}
      <div className="flex items-center gap-0.5">
        <ToolbarSeparator />
        <button
          className={cn(
            'p-1.5 rounded transition-colors',
            isPlaying
              ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
              : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
          )}
          onClick={handlePlayPause}
          title={isPlaying ? 'Pause (Ctrl+Shift+P)' : 'Play (Ctrl+P)'}
        >
          {isPlaying ? <Pause size={18} /> : <Play size={18} />}
        </button>
        {(isPlaying || isPaused) && (
          <button
            className="p-1.5 rounded text-red-400 hover:text-red-300 hover:bg-zinc-700 transition-colors"
            onClick={handleStop}
            title="Stop"
          >
            <Square size={18} />
          </button>
        )}
      </div>

      {/* Right: Zoom & Reset */}
      <div className="flex items-center gap-1">
        {/* Zoom Dropdown */}
        <div className="relative">
          <button
            className="flex items-center gap-1 px-2 py-1 rounded text-sm text-zinc-300 hover:bg-zinc-700 transition-colors min-w-[70px] justify-between"
            onClick={() => setZoomDropdownOpen(!zoomDropdownOpen)}
            title="Zoom Level"
          >
            <span className="font-mono">{zoomPercentage}%</span>
            <ChevronDown size={14} />
          </button>

          {zoomDropdownOpen && (
            <>
              {/* Backdrop to close dropdown */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setZoomDropdownOpen(false)}
              />

              <div className="absolute right-0 top-full mt-1 z-50 bg-zinc-800 border border-zinc-700 rounded-lg shadow-lg py-1 min-w-[100px]">
                {ZOOM_PRESETS.map((zoom) => (
                  <button
                    key={zoom}
                    className={cn(
                      'w-full px-3 py-1.5 text-left text-sm hover:bg-zinc-700 transition-colors',
                      viewport.zoom === zoom
                        ? 'text-blue-400 bg-zinc-700/50'
                        : 'text-zinc-300'
                    )}
                    onClick={() => handleZoomSelect(zoom)}
                  >
                    {Math.round(zoom * 100)}%
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <ToolbarSeparator />

        {/* Reset View Button */}
        <button
          className="p-1.5 rounded text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
          onClick={handleResetView}
          title="Reset View (Home)"
        >
          <Home size={18} />
        </button>
      </div>
    </div>
  );
};
